import apiClient from './api/client'
import type {
  ServiceRequest,
  CreateServiceRequest,
  SetPriceRequest,
  DeclineRequest,
  AcceptRequest,
} from '../types/serviceRequest'
import type { PaginatedResponse } from '../types'

export const serviceRequestService = {
  // Get service requests (filtered by user role on backend)
  async getRequests(status?: string): Promise<PaginatedResponse<ServiceRequest>> {
    const { data } = await apiClient.get('/connections/service-requests/', {
      params: status ? { status } : undefined,
    })
    return data
  },

  // Get a single service request
  async getRequest(id: string): Promise<ServiceRequest> {
    const { data } = await apiClient.get(`/connections/service-requests/${id}/`)
    return data
  },

  // Create a service request (seeker only)
  async createRequest(request: CreateServiceRequest): Promise<ServiceRequest> {
    const { data } = await apiClient.post('/connections/service-requests/', request)
    return data
  },

  // Accept a request (expert only)
  async accept(requestId: string, acceptData?: AcceptRequest): Promise<ServiceRequest> {
    const { data } = await apiClient.post(
      `/connections/service-requests/${requestId}/accept/`,
      acceptData || {}
    )
    return data
  },

  // Set price for a request (expert only)
  async setPrice(requestId: string, priceData: SetPriceRequest): Promise<ServiceRequest> {
    const { data } = await apiClient.post(
      `/connections/service-requests/${requestId}/set_price/`,
      priceData
    )
    return data
  },

  // Decline a request (expert only)
  async decline(requestId: string, declineData?: DeclineRequest): Promise<ServiceRequest> {
    const { data } = await apiClient.post(
      `/connections/service-requests/${requestId}/decline/`,
      declineData || {}
    )
    return data
  },

  // Decline the quoted price (seeker only)
  async declinePrice(requestId: string): Promise<ServiceRequest> {
    const { data } = await apiClient.post(
      `/connections/service-requests/${requestId}/decline_price/`
    )
    return data
  },

  // Create Stripe checkout session for payment (seeker only)
  async createCheckout(
    requestId: string
  ): Promise<{ checkout_url: string; session_id: string; payment_id: string }> {
    const { data } = await apiClient.post('/payments/service/', {
      service_request_id: requestId,
      success_url: `${window.location.origin}/connections?payment=success&session_id={CHECKOUT_SESSION_ID}`,
      cancel_url: `${window.location.origin}/connections?payment=cancelled`,
    })
    return data
  },

  // Verify payment after Stripe redirect
  async verifyPayment(sessionId: string): Promise<{ status: string }> {
    const { data } = await apiClient.post('/payments/verify/', {
      session_id: sessionId,
    })
    return data
  },

  // Mark a service request as completed (seeker only)
  async markCompleted(requestId: string): Promise<ServiceRequest> {
    const { data } = await apiClient.post(
      `/connections/service-requests/${requestId}/mark_completed/`
    )
    return data
  },

  // Get badge counts for unseen collaboration events per tab
  async getBadgeCounts(): Promise<Record<string, number>> {
    const { data } = await apiClient.get('/connections/service-requests/badge_counts/')
    return data
  },

  // Mark a collaboration tab as viewed (clears badge for that tab)
  async markTabViewed(tab: string): Promise<void> {
    await apiClient.post('/connections/service-requests/mark_tab_viewed/', { tab })
  },
}

export default serviceRequestService
